import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const API_BASE = import.meta.env['VITE_API_URL'] ?? 'http://localhost:3200';
const YEAR = new Date().getFullYear();

function FloatingCard({
  className,
  children,
  delay = 0,
  style,
}: {
  className?: string;
  children: React.ReactNode;
  delay?: number;
  style?: React.CSSProperties;
}) {
  return (
    <div
      className={`absolute rounded-2xl p-3.5 shadow-xl ${className ?? ''}`}
      style={{
        background: 'rgba(255,255,255,0.08)',
        backdropFilter: 'blur(16px)',
        border: '1px solid rgba(255,255,255,0.15)',
        animation: `floatCard 5s ease-in-out ${delay}s infinite alternate`,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

/* Stylised Uganda map outline as an SVG path */
function UgandaMapSilhouette() {
  return (
    <svg
      viewBox="0 0 220 260"
      className="absolute opacity-10"
      style={{ bottom: 0, right: '-10%', width: 320, height: 380 }}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M110 10 C130 8, 155 18, 170 35 C185 52, 195 68, 200 88
           C208 112, 205 135, 195 155 C185 175, 170 188, 158 205
           C145 222, 135 238, 118 248 C108 254, 98 256, 88 250
           C75 242, 65 228, 55 212 C42 192, 30 172, 22 150
           C14 128, 12 104, 18 82 C24 60, 38 42, 55 28
           C72 14, 90 12, 110 10Z"
        fill="url(#ugandaFill)"
        stroke="rgba(251,191,36,0.4)"
        strokeWidth="1.5"
      />
      <defs>
        <linearGradient id="ugandaFill" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#b45309" stopOpacity="0.5" />
          <stop offset="100%" stopColor="#166534" stopOpacity="0.3" />
        </linearGradient>
      </defs>

      {/* City pin dots */}
      {[
        { cx: 108, cy: 148, label: 'Kampala' },
        { cx: 88, cy: 120, label: 'Gulu' },
        { cx: 148, cy: 168, label: 'Jinja' },
        { cx: 72, cy: 172, label: 'Mbarara' },
        { cx: 130, cy: 105, label: 'Mbale' },
      ].map((p) => (
        <g key={p.label}>
          <circle cx={p.cx} cy={p.cy} r="5" fill="rgba(251,191,36,0.7)" />
          <circle cx={p.cx} cy={p.cy} r="9" fill="none" stroke="rgba(251,191,36,0.3)" strokeWidth="1.5" />
        </g>
      ))}
    </svg>
  );
}

export function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [mounted, setMounted] = useState(false);

  useEffect(() => { setMounted(true); }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/v1/auth/login-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      });
      const json = await res.json();
      if (!res.ok) {
        setError(json?.error?.message ?? 'Invalid email or password. Please try again.');
        return;
      }
      const { accessToken: token, user } = json.data;
      localStorage.setItem('urs-auth', JSON.stringify({
        state: { token, userId: user.id, role: user.role, tenantId: user.tenantId, locationId: user.locationId },
        version: 0,
      }));
      navigate('/dashboard', { replace: true });
    } catch {
      setError('Unable to connect to the server. Check your network and try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <style>{`
        @keyframes floatCard {
          from { transform: translateY(0px) rotate(0deg); }
          to   { transform: translateY(-14px) rotate(0.5deg); }
        }
        @keyframes spinSlow {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        @keyframes spinSlowRev {
          from { transform: rotate(0deg); }
          to   { transform: rotate(-360deg); }
        }
        @keyframes pulse-ring {
          0%   { transform: scale(1);   opacity: 0.6; }
          100% { transform: scale(1.8); opacity: 0; }
        }
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(28px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <div className="min-h-screen flex overflow-hidden" style={{ background: '#0d0a04' }}>

        {/* ══════════════════════════════════════════════════
            LEFT PANEL — Deep earth tones + Uganda identity
        ══════════════════════════════════════════════════ */}
        <div
          className="hidden lg:flex lg:w-[54%] relative flex-col overflow-hidden"
          style={{
            background: 'linear-gradient(160deg, #0d0a04 0%, #1c1004 25%, #0a1a0a 55%, #12080a 100%)',
          }}
        >
          {/* Deep colour blobs */}
          <div className="absolute inset-0 pointer-events-none">
            {/* Amber/gold — top left, Ugandan gold */}
            <div className="absolute top-[-8%] left-[-8%] w-[480px] h-[480px] rounded-full"
              style={{ background: 'radial-gradient(circle, rgba(180,83,9,0.28) 0%, transparent 65%)' }} />
            {/* Forest green — centre, Ugandan green */}
            <div className="absolute top-[35%] left-[10%] w-[380px] h-[380px] rounded-full"
              style={{ background: 'radial-gradient(circle, rgba(22,101,52,0.22) 0%, transparent 65%)' }} />
            {/* Red — bottom right, Ugandan red */}
            <div className="absolute bottom-[-5%] right-[-5%] w-[340px] h-[340px] rounded-full"
              style={{ background: 'radial-gradient(circle, rgba(153,27,27,0.20) 0%, transparent 65%)' }} />
            {/* Warm highlight centre */}
            <div className="absolute top-[55%] left-[50%] w-[260px] h-[260px] rounded-full"
              style={{ background: 'radial-gradient(circle, rgba(251,191,36,0.06) 0%, transparent 70%)' }} />
          </div>

          {/* Dot grid */}
          <svg className="absolute inset-0 w-full h-full opacity-[0.14]" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="dotgrid" width="30" height="30" patternUnits="userSpaceOnUse">
                <circle cx="1.5" cy="1.5" r="1.5" fill="rgba(251,191,36,0.55)" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#dotgrid)" />
          </svg>

          {/* Uganda flag stripe accent — very subtle vertical bars */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden opacity-[0.04]">
            {['#1a1a1a','#b45309','#dc2626','#1a1a1a','#166534','#1a1a1a'].map((c, i) => (
              <div key={i} className="absolute top-0 bottom-0" style={{
                left: `${i * 16.6}%`, width: '16.6%', background: c,
              }} />
            ))}
          </div>

          {/* Uganda map silhouette */}
          <UgandaMapSilhouette />

          {/* Decorative spinning rings */}
          <div className="absolute pointer-events-none"
            style={{ top: '8%', right: '6%', width: 160, height: 160 }}>
            <div className="absolute inset-0 rounded-full border-2 border-dashed opacity-20"
              style={{ borderColor: 'rgba(251,191,36,0.8)', animation: 'spinSlow 24s linear infinite' }} />
            <div className="absolute inset-4 rounded-full border border-dashed opacity-15"
              style={{ borderColor: 'rgba(22,101,52,0.9)', animation: 'spinSlowRev 16s linear infinite' }} />
            {/* Pulse dot at centre */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-3 h-3 rounded-full" style={{ background: '#fbbf24' }}>
                <div className="absolute w-3 h-3 rounded-full"
                  style={{ background: '#fbbf24', animation: 'pulse-ring 2s ease-out infinite' }} />
              </div>
            </div>
          </div>

          {/* ── Logo ── */}
          <div className="relative z-10 flex items-center gap-3 px-10 pt-9">
            <div className="w-11 h-11 rounded-2xl flex items-center justify-center shadow-xl"
              style={{ background: 'linear-gradient(135deg, #b45309, #dc2626)' }}>
              <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round"
                  d="M12 2C8 2 5 5 5 9c0 2.4 1.1 4.5 2.8 5.9L7 20h10l-.8-5.1C17.9 13.5 19 11.4 19 9c0-4-3-7-7-7z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 20v1a3 3 0 006 0v-1" />
              </svg>
            </div>
            <div>
              <p className="text-white font-black text-xl leading-none tracking-tight">URS</p>
              <p className="text-base font-semibold leading-none mt-1" style={{ color: 'rgba(251,191,36,0.6)' }}>
                by{' '}
                <a href="https://www.zoelan.com" target="_blank" rel="noopener noreferrer"
                  style={{ color: '#fbbf24', textDecoration: 'none', fontWeight: 700 }}
                  onMouseEnter={(e) => { (e.target as HTMLElement).style.color = '#fde68a'; (e.target as HTMLElement).style.textDecoration = 'underline'; }}
                  onMouseLeave={(e) => { (e.target as HTMLElement).style.color = '#fbbf24'; (e.target as HTMLElement).style.textDecoration = 'none'; }}
                >zoelan</a>
              </p>
            </div>
          </div>

          {/* ── Hero text ── */}
          <div className="relative z-10 px-10 mt-9">
            <div className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 mb-5"
              style={{ background: 'rgba(251,146,60,0.12)', border: '1px solid rgba(251,146,60,0.28)' }}>
              <span className="w-2 h-2 bg-amber-400 rounded-full animate-pulse" />
              <span className="text-amber-400 text-[11px] font-bold tracking-widest uppercase">
                Uganda's #1 Restaurant Platform
              </span>
            </div>

            <h1 className="text-[2.6rem] font-black leading-[1.12] mb-4" style={{ color: '#f5f0e8' }}>
              Run your restaurant<br />
              <span style={{
                background: 'linear-gradient(90deg, #fbbf24 0%, #f97316 40%, #dc2626 75%, #166534 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}>
                smarter, faster.
              </span>
            </h1>

            <p className="text-sm leading-relaxed max-w-xs" style={{ color: 'rgba(245,240,232,0.55)' }}>
              From POS to payroll, inventory to EFRIS — everything Uganda's restaurants need, built locally.
            </p>
          </div>

          {/* ── Dashboard mockup ── */}
          <div className="relative z-10 flex-1 px-10 mt-7" style={{ minHeight: 240 }}>

            {/* Central card */}
            <div className="rounded-2xl overflow-hidden" style={{
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.1)',
              backdropFilter: 'blur(10px)',
              maxWidth: 400,
            }}>
              {/* Window chrome */}
              <div className="flex items-center gap-2 px-4 py-2.5" style={{
                background: 'rgba(255,255,255,0.04)',
                borderBottom: '1px solid rgba(255,255,255,0.07)',
              }}>
                <div className="w-2.5 h-2.5 rounded-full" style={{ background: 'rgba(220,38,38,0.6)' }} />
                <div className="w-2.5 h-2.5 rounded-full" style={{ background: 'rgba(251,191,36,0.6)' }} />
                <div className="w-2.5 h-2.5 rounded-full" style={{ background: 'rgba(22,163,74,0.6)' }} />
                <div className="ml-2 h-2 rounded-full flex-1" style={{ background: 'rgba(255,255,255,0.07)', maxWidth: 110 }} />
              </div>

              {/* Stat row */}
              <div className="grid grid-cols-3 gap-2.5 p-3.5">
                {[
                  { label: "Today's Sales", val: 'UGX 847K', color: '#fbbf24', arrow: '▲', arrowColor: '#4ade80' },
                  { label: 'Orders',        val: '142',      color: '#f97316', arrow: '▲', arrowColor: '#4ade80' },
                  { label: 'Tables',        val: '18 / 24',  color: '#22d3ee', arrow: '▼', arrowColor: '#f87171' },
                ].map((s) => (
                  <div key={s.label} className="rounded-xl p-2.5" style={{
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.07)',
                  }}>
                    <p className="text-[9px] uppercase tracking-wide mb-1" style={{ color: 'rgba(245,240,232,0.4)' }}>{s.label}</p>
                    <p className="font-black text-sm leading-none" style={{ color: s.color }}>{s.val}</p>
                    <p className="text-[9px] mt-1" style={{ color: s.arrowColor }}>{s.arrow} vs yesterday</p>
                  </div>
                ))}
              </div>

              {/* Bar chart */}
              <div className="px-3.5 pb-3.5">
                <div className="flex items-end gap-1 h-14">
                  {[35, 58, 42, 75, 50, 88, 65, 54, 80, 46, 70, 95].map((h, i) => (
                    <div key={i} className="flex-1 rounded-t" style={{
                      height: `${h}%`,
                      background: i === 11
                        ? 'linear-gradient(180deg, #fbbf24, #b45309)'
                        : i % 3 === 1
                          ? 'rgba(22,101,52,0.35)'
                          : 'rgba(251,191,36,0.15)',
                    }} />
                  ))}
                </div>
                <div className="flex justify-between mt-1.5 px-0.5">
                  {['M','T','W','T','F','S','S'].map((d, i) => (
                    <p key={i} className="text-[9px]" style={{ color: 'rgba(245,240,232,0.3)' }}>{d}</p>
                  ))}
                </div>
              </div>
            </div>

            {/* Floating card — new order */}
            <FloatingCard className="w-44" delay={0} style={{ top: -18, right: 20 }}>
              <div className="flex items-center gap-2 mb-1.5">
                <div className="w-7 h-7 rounded-xl flex items-center justify-center text-sm"
                  style={{ background: 'rgba(251,146,60,0.2)' }}>🍽️</div>
                <p className="text-[10px] uppercase tracking-wide" style={{ color: 'rgba(245,240,232,0.5)' }}>New Order</p>
              </div>
              <p className="text-white font-bold text-sm">Table 7 — 4 pax</p>
              <p className="text-xs mt-0.5" style={{ color: '#4ade80' }}>UGX 156,000</p>
            </FloatingCard>

            {/* Floating card — MoMo */}
            <FloatingCard className="w-40" delay={1.8} style={{ bottom: 10, right: 10 }}>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-7 h-7 rounded-xl flex items-center justify-center text-sm"
                  style={{ background: 'rgba(22,101,52,0.25)' }}>📱</div>
                <p className="text-[10px] uppercase tracking-wide" style={{ color: 'rgba(245,240,232,0.5)' }}>MoMo Pay</p>
              </div>
              <p className="text-white font-bold text-sm">UGX 89,500</p>
              <div className="flex items-center gap-1.5 mt-1">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                <p className="text-[10px]" style={{ color: '#4ade80' }}>MTN Confirmed</p>
              </div>
            </FloatingCard>

            {/* Floating card — EFRIS */}
            <FloatingCard className="w-36" delay={0.9} style={{ bottom: 80, left: -8 }}>
              <p className="text-[10px] uppercase tracking-wide mb-1.5" style={{ color: 'rgba(245,240,232,0.5)' }}>EFRIS / URA</p>
              <div className="flex items-center gap-1.5">
                <div className="w-5 h-5 rounded-full flex items-center justify-center"
                  style={{ background: 'rgba(74,222,128,0.15)' }}>
                  <div className="w-2 h-2 rounded-full bg-emerald-400" />
                </div>
                <p className="text-white text-xs font-semibold">Receipt Sent</p>
              </div>
              <p className="text-[9px] mt-1" style={{ color: 'rgba(245,240,232,0.3)' }}>Auto-submitted ✓</p>
            </FloatingCard>

          </div>

          {/* ── Feature pills ── */}
          <div className="relative z-10 px-10 pb-9 mt-5 flex flex-wrap gap-2">
            {[
              { icon: '📊', label: 'Analytics' },
              { icon: '📱', label: 'MTN / Airtel MoMo' },
              { icon: '🧾', label: 'EFRIS Compliant' },
              { icon: '📶', label: 'Offline POS' },
              { icon: '👥', label: 'HR & Payroll' },
            ].map((f) => (
              <span key={f.label} className="flex items-center gap-1.5 text-xs rounded-full px-3 py-1.5"
                style={{
                  color: 'rgba(245,240,232,0.65)',
                  background: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(255,255,255,0.1)',
                }}>
                <span>{f.icon}</span> {f.label}
              </span>
            ))}
          </div>
        </div>

        {/* ══════════════════════════════════════════════════
            RIGHT PANEL — Warm parchment / cream
        ══════════════════════════════════════════════════ */}
        <div
          className="flex-1 flex flex-col relative overflow-hidden"
          style={{ background: 'linear-gradient(160deg, #fdf6ec 0%, #fef3e2 45%, #f5ede0 100%)' }}
        >
          {/* Soft background blobs */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div className="absolute top-[-80px] right-[-60px] w-96 h-96 rounded-full"
              style={{ background: 'radial-gradient(circle, rgba(180,83,9,0.10) 0%, transparent 65%)' }} />
            <div className="absolute bottom-[-60px] left-[-40px] w-80 h-80 rounded-full"
              style={{ background: 'radial-gradient(circle, rgba(22,101,52,0.09) 0%, transparent 65%)' }} />
            <div className="absolute top-[38%] right-[8%] w-72 h-72 rounded-full"
              style={{ background: 'radial-gradient(circle, rgba(220,38,38,0.06) 0%, transparent 65%)' }} />
          </div>

          {/* Dot grid */}
          <svg className="absolute inset-0 w-full h-full opacity-25" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="dotgrid2" width="22" height="22" patternUnits="userSpaceOnUse">
                <circle cx="1.5" cy="1.5" r="1" fill="rgba(180,83,9,0.3)" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#dotgrid2)" />
          </svg>

          {/* Decorative rings — bottom right */}
          <svg className="absolute bottom-0 right-0 opacity-[0.07]" width="340" height="340" viewBox="0 0 340 340">
            <circle cx="300" cy="300" r="220" fill="none" stroke="#b45309" strokeWidth="44" />
            <circle cx="240" cy="240" r="140" fill="none" stroke="#166534" strokeWidth="28" />
            <circle cx="300" cy="300" r="90"  fill="none" stroke="#dc2626" strokeWidth="16" />
          </svg>

          {/* Decorative rings — top left */}
          <svg className="absolute top-0 left-0 opacity-[0.05]" width="240" height="240" viewBox="0 0 240 240">
            <circle cx="0" cy="0" r="180" fill="none" stroke="#b45309" strokeWidth="36" />
            <circle cx="0" cy="0" r="100" fill="none" stroke="#166534" strokeWidth="20" />
          </svg>

          {/* ── Top stat cards (top-left) ── */}
          <div className="hidden lg:flex absolute top-8 left-8 flex-col gap-3 z-10"
            style={{ opacity: mounted ? 1 : 0, transition: 'opacity 1s ease 0.3s' }}>
            {[
              { icon: '🍽️', label: "Today's Orders", val: '142',       sub: '▲ 18 vs yesterday', subColor: '#16a34a' },
              { icon: '💰', label: "Today's Revenue", val: 'UGX 847K', sub: '▲ 12% vs yesterday', subColor: '#16a34a' },
            ].map((s, i) => (
              <div key={s.label} className="flex items-center gap-3 rounded-2xl px-4 py-3"
                style={{
                  background: 'rgba(255,252,245,0.80)',
                  backdropFilter: 'blur(16px)',
                  border: '1.5px solid rgba(255,255,255,0.95)',
                  boxShadow: '0 6px 20px rgba(180,83,9,0.10)',
                  animation: `floatCard 5s ease-in-out ${i * 1.2}s infinite alternate`,
                }}>
                <div className="w-9 h-9 rounded-xl flex items-center justify-center text-lg"
                  style={{ background: 'rgba(180,83,9,0.10)' }}>
                  {s.icon}
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-widest font-bold" style={{ color: '#a8a29e' }}>{s.label}</p>
                  <p className="text-base font-black leading-tight" style={{ color: '#1c0a00' }}>{s.val}</p>
                  <p className="text-[10px] font-semibold mt-0.5" style={{ color: s.subColor }}>{s.sub}</p>
                </div>
              </div>
            ))}
          </div>

          {/* ── Feature badges (top-right) ── */}
          <div className="hidden lg:flex absolute top-8 right-8 flex-col gap-2.5 z-10"
            style={{ opacity: mounted ? 1 : 0, transition: 'opacity 1s ease 0.5s' }}>
            {[
              { icon: '🧾', label: 'EFRIS / URA', val: 'Tax Compliant', accent: '#b45309' },
              { icon: '📶', label: 'POS System',  val: 'Offline-first',  accent: '#166534' },
              { icon: '📱', label: 'Payments',    val: 'MTN & Airtel',   accent: '#dc2626' },
            ].map((b, i) => (
              <div key={b.label} className="flex items-center gap-3 rounded-2xl px-4 py-2.5"
                style={{
                  background: 'rgba(255,252,245,0.80)',
                  backdropFilter: 'blur(16px)',
                  border: '1.5px solid rgba(255,255,255,0.95)',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.05)',
                  animation: `floatCard 5s ease-in-out ${i * 0.9 + 0.5}s infinite alternate`,
                }}>
                <div className="w-8 h-8 rounded-xl flex items-center justify-center text-sm"
                  style={{ background: `${b.accent}18` }}>
                  {b.icon}
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-widest font-bold" style={{ color: '#a8a29e' }}>{b.label}</p>
                  <p className="text-sm font-black" style={{ color: '#1c0a00' }}>{b.val}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Mobile top bar */}
          <div className="relative z-10 flex items-center px-8 pt-7 lg:hidden">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center shadow"
                style={{ background: 'linear-gradient(135deg, #b45309, #dc2626)' }}>
                <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round"
                    d="M12 2C8 2 5 5 5 9c0 2.4 1.1 4.5 2.8 5.9L7 20h10l-.8-5.1C17.9 13.5 19 11.4 19 9c0-4-3-7-7-7z" />
                </svg>
              </div>
              <div>
                <p className="font-black text-stone-800 text-lg leading-none">URS</p>
                <p className="text-base font-semibold leading-none mt-1" style={{ color: 'rgba(180,83,9,0.7)' }}>
                  by{' '}
                  <a href="https://www.zoelan.com" target="_blank" rel="noopener noreferrer"
                    style={{ color: '#b45309', textDecoration: 'none', fontWeight: 700 }}
                    onMouseEnter={(e) => { (e.target as HTMLElement).style.color = '#d97706'; (e.target as HTMLElement).style.textDecoration = 'underline'; }}
                    onMouseLeave={(e) => { (e.target as HTMLElement).style.color = '#b45309'; (e.target as HTMLElement).style.textDecoration = 'none'; }}
                  >zoelan</a>
                </p>
              </div>
            </div>
          </div>

          {/* ── Form (centre) ── */}
          <div className="relative z-10 flex-1 flex items-center justify-center px-8 py-8">
            <div className="w-full" style={{
              maxWidth: 400,
              opacity: mounted ? 1 : 0,
              transform: mounted ? 'translateY(0)' : 'translateY(22px)',
              transition: 'opacity 0.75s ease, transform 0.75s ease',
            }}>
              {/* Glass-parchment card */}
              <div className="rounded-3xl p-8" style={{
                background: 'rgba(255,252,245,0.85)',
                backdropFilter: 'blur(28px)',
                border: '1.5px solid rgba(255,255,255,0.95)',
                boxShadow: '0 24px 64px rgba(180,83,9,0.12), 0 4px 20px rgba(0,0,0,0.06)',
              }}>
                {/* Card header */}
                <div className="flex items-center gap-3 mb-7">
                  <div className="w-11 h-11 rounded-2xl flex items-center justify-center shadow-lg"
                    style={{ background: 'linear-gradient(135deg, #b45309, #dc2626)' }}>
                    <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round"
                        d="M12 2C8 2 5 5 5 9c0 2.4 1.1 4.5 2.8 5.9L7 20h10l-.8-5.1C17.9 13.5 19 11.4 19 9c0-4-3-7-7-7z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 20v1a3 3 0 006 0v-1" />
                    </svg>
                  </div>
                  {/* Uganda flag mini stripe */}
                  <div className="flex gap-1 items-center">
                    {['#1a1a1a','#b45309','#dc2626','#1a1a1a','#166534'].map((c, i) => (
                      <div key={i} className="w-2 h-6 rounded-sm" style={{ background: c, opacity: 0.75 }} />
                    ))}
                  </div>
                </div>

                <h2 className="text-2xl font-black mb-1" style={{ color: '#1c0a00' }}>
                  Sign in to your account
                </h2>
                <p className="text-sm mb-7" style={{ color: '#78716c' }}>
                  Access your restaurant management dashboard
                </p>

                {/* Error */}
                {error && (
                  <div className="flex items-start gap-3 rounded-xl p-3.5 mb-5"
                    style={{ background: 'rgba(220,38,38,0.07)', border: '1px solid rgba(220,38,38,0.18)' }}>
                    <svg className="w-4 h-4 mt-0.5 shrink-0 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                    </svg>
                    <p className="text-red-700 text-sm leading-snug">{error}</p>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Email */}
                  <div>
                    <label className="block text-[10px] font-black uppercase tracking-widest mb-1.5"
                      style={{ color: '#78716c' }}>Email Address</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <svg className="w-4 h-4" style={{ color: '#a8a29e' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                        </svg>
                      </div>
                      <input type="email" autoComplete="email" required value={email}
                        onChange={(e) => setEmail(e.target.value)} placeholder="you@restaurant.ug"
                        className="w-full pl-11 pr-4 py-3 text-sm outline-none transition-all"
                        style={{ background: '#fdf8f3', border: '1.5px solid #e7ddd4', borderRadius: 12, color: '#1c0a00' }}
                        onFocus={(e) => { e.target.style.borderColor = '#b45309'; e.target.style.boxShadow = '0 0 0 3px rgba(180,83,9,0.1)'; }}
                        onBlur={(e)  => { e.target.style.borderColor = '#e7ddd4'; e.target.style.boxShadow = 'none'; }}
                      />
                    </div>
                  </div>

                  {/* Password */}
                  <div>
                    <label className="block text-[10px] font-black uppercase tracking-widest mb-1.5"
                      style={{ color: '#78716c' }}>Password</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <svg className="w-4 h-4" style={{ color: '#a8a29e' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                        </svg>
                      </div>
                      <input type={showPassword ? 'text' : 'password'} autoComplete="current-password"
                        required value={password} onChange={(e) => setPassword(e.target.value)}
                        placeholder="••••••••"
                        className="w-full pl-11 pr-12 py-3 text-sm outline-none transition-all"
                        style={{ background: '#fdf8f3', border: '1.5px solid #e7ddd4', borderRadius: 12, color: '#1c0a00' }}
                        onFocus={(e) => { e.target.style.borderColor = '#b45309'; e.target.style.boxShadow = '0 0 0 3px rgba(180,83,9,0.1)'; }}
                        onBlur={(e)  => { e.target.style.borderColor = '#e7ddd4'; e.target.style.boxShadow = 'none'; }}
                      />
                      <button type="button" onClick={() => setShowPassword((v) => !v)}
                        className="absolute inset-y-0 right-0 pr-4 flex items-center transition-colors"
                        style={{ color: '#a8a29e' }}>
                        {showPassword ? (
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                          </svg>
                        ) : (
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>

                  {/* CTA */}
                  <button type="submit" disabled={loading || !email || !password}
                    className="w-full text-white font-bold rounded-xl py-3.5 text-sm mt-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{
                      background: 'linear-gradient(135deg, #b45309 0%, #dc2626 50%, #166534 100%)',
                      boxShadow: '0 8px 28px rgba(180,83,9,0.30)',
                    }}
                    onMouseEnter={(e) => {
                      if (!e.currentTarget.disabled) {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 14px 36px rgba(180,83,9,0.45)';
                        e.currentTarget.style.filter = 'brightness(1.12)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 8px 28px rgba(180,83,9,0.30)';
                      e.currentTarget.style.filter = 'brightness(1)';
                    }}>
                    {loading ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Signing in…
                      </span>
                    ) : 'Sign in to Dashboard'}
                  </button>
                </form>

                {/* Uganda flag stripe */}
                <div className="flex mt-7 rounded-full overflow-hidden h-1.5">
                  {['#1a1a1a','#b45309','#dc2626','#1a1a1a','#166534','#1a1a1a'].map((c, i) => (
                    <div key={i} className="flex-1" style={{ background: c }} />
                  ))}
                </div>
              </div>

              <p className="text-center text-sm mt-5" style={{ color: '#44403c' }}>
                © {YEAR} Uganda Restaurant System &nbsp;·&nbsp;
                <a href="https://www.zoelan.com" target="_blank" rel="noopener noreferrer"
                  className="transition-colors hover:opacity-60 font-bold" style={{ color: '#b45309' }}>
                  Powered by Zoelan
                </a>
              </p>
            </div>
          </div>

          {/* ── Live activity strip — bottom ── */}
          <div className="hidden lg:flex absolute bottom-8 left-1/2 z-10 gap-3"
            style={{
              transform: 'translateX(-50%)',
              opacity: mounted ? 1 : 0,
              transition: 'opacity 1s ease 0.7s',
            }}>
            {[
              { dot: '#16a34a', text: 'Table 3 — Order placed', time: 'just now' },
              { dot: '#b45309', text: 'MoMo payment confirmed', time: '1 min ago' },
              { dot: '#dc2626', text: 'EFRIS receipt submitted', time: '2 min ago' },
            ].map((a) => (
              <div key={a.text} className="flex items-center gap-2.5 rounded-2xl px-4 py-2.5"
                style={{
                  background: 'rgba(255,252,245,0.80)',
                  backdropFilter: 'blur(16px)',
                  border: '1.5px solid rgba(255,255,255,0.95)',
                  boxShadow: '0 4px 14px rgba(0,0,0,0.06)',
                  whiteSpace: 'nowrap',
                }}>
                <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: a.dot }} />
                <p className="text-xs font-semibold" style={{ color: '#1c0a00' }}>{a.text}</p>
                <p className="text-[10px]" style={{ color: '#a8a29e' }}>{a.time}</p>
              </div>
            ))}
          </div>

        </div>
      </div>
    </>
  );
}
